import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Mic, MonitorUp, Pause, Play, RefreshCw, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useMediaRecorder } from '../hooks/use-media-recorder'
import {
  formatRecordingDuration,
  listAudioInputDevices,
  type AudioInputDeviceOption,
  type MediaRecordingKind,
} from '../utils/media-recording'
import { cn } from '@/shared/ui/cn'
import {
  AUDIO_METER_SCALE_MARKS,
  dbMarkToPercent,
  formatMeterDb,
  linearLevelToPercent,
} from '@/features/editor/components/audio-meter-utils'

const RECORDING_METER_SEGMENT_MASK =
  'repeating-linear-gradient(to top, #000 0 7px, transparent 7px 10px)'
const RECORDING_METER_UNLIT_BG =
  'repeating-linear-gradient(to top, rgba(255,255,255,0.05) 0 7px, transparent 7px 10px)'

function animateMeterChannel(
  channel: { displayPercent: number; peakPercent: number; peakHoldSeconds: number },
  targetPercent: number,
  deltaSeconds: number,
): void {
  if (targetPercent >= channel.displayPercent) {
    const attackFactor = 1 - Math.exp(-28 * deltaSeconds)
    channel.displayPercent += (targetPercent - channel.displayPercent) * attackFactor
  } else {
    channel.displayPercent = Math.max(targetPercent, channel.displayPercent - 76 * deltaSeconds)
  }

  if (channel.displayPercent >= channel.peakPercent) {
    channel.peakPercent = channel.displayPercent
    channel.peakHoldSeconds = 0.22
  } else if (channel.peakHoldSeconds > 0) {
    channel.peakHoldSeconds = Math.max(0, channel.peakHoldSeconds - deltaSeconds)
  } else {
    channel.peakPercent = Math.max(channel.displayPercent, channel.peakPercent - 54 * deltaSeconds)
  }
}

interface MediaRecordingDialogProps {
  open: boolean
  kind: MediaRecordingKind
  onOpenChange: (open: boolean) => void
  onSaveRecording: (file: File) => Promise<void>
}

interface MeterChannelState {
  displayPercent: number
  peakPercent: number
  peakHoldSeconds: number
}

interface FloatingButtonPosition {
  x: number
  y: number
}

const FLOATING_BUTTON_WIDTH = 236
const FLOATING_BUTTON_HEIGHT = 72
const FLOATING_BUTTON_MARGIN = 24

function clampFloatingPosition(position: FloatingButtonPosition): FloatingButtonPosition {
  if (typeof window === 'undefined') {
    return position
  }

  return {
    x: Math.min(
      Math.max(FLOATING_BUTTON_MARGIN, position.x),
      window.innerWidth - FLOATING_BUTTON_WIDTH - FLOATING_BUTTON_MARGIN,
    ),
    y: Math.min(
      Math.max(FLOATING_BUTTON_MARGIN, position.y),
      window.innerHeight - FLOATING_BUTTON_HEIGHT - FLOATING_BUTTON_MARGIN,
    ),
  }
}

function getDefaultFloatingPosition(): FloatingButtonPosition {
  if (typeof window === 'undefined') {
    return { x: 0, y: 0 }
  }

  return {
    x: window.innerWidth - FLOATING_BUTTON_WIDTH - FLOATING_BUTTON_MARGIN,
    y: window.innerHeight - FLOATING_BUTTON_HEIGHT - FLOATING_BUTTON_MARGIN,
  }
}

export function MediaRecordingDialog({
  open,
  kind,
  onOpenChange,
  onSaveRecording,
}: MediaRecordingDialogProps) {
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isRefreshingDevices, setIsRefreshingDevices] = useState(false)
  const [audioInputDevices, setAudioInputDevices] = useState<AudioInputDeviceOption[]>([])
  const [selectedAudioInputDeviceId, setSelectedAudioInputDeviceId] = useState<string>('default')
  const [includeScreenMicrophone, setIncludeScreenMicrophone] = useState(true)
  const [isScreenMinimized, setIsScreenMinimized] = useState(false)
  const [floatingButtonPosition, setFloatingButtonPosition] = useState<FloatingButtonPosition | null>(null)
  const meterVisualRootRef = useRef<HTMLDivElement | null>(null)
  const floatingDragRef = useRef<{
    startPointerX: number
    startPointerY: number
    startPosition: FloatingButtonPosition
    moved: boolean
  } | null>(null)
  const meterTargetPercentRef = useRef({ left: 0, right: 0 })
  const meterAnimationRef = useRef({
    left: { displayPercent: 0, peakPercent: 0, peakHoldSeconds: 0 } as MeterChannelState,
    right: { displayPercent: 0, peakPercent: 0, peakHoldSeconds: 0 } as MeterChannelState,
    lastTimestamp: 0,
  })
  const meterFrameRef = useRef<number | null>(null)
  const { supported, status, elapsedSeconds, audioLevels, errorMessage, startRecording, stopRecording, pauseRecording, resumeRecording, cancelRecording, reset } =
    useMediaRecorder(kind)

  const title = kind === 'audio' ? '语音录制' : '屏幕录制'
  const description =
    kind === 'audio'
      ? '使用麦克风录制一段语音，停止后会自动写入当前项目媒体库。'
      : '录制当前屏幕画面，停止后会自动写入当前项目媒体库。'
  const Icon = kind === 'audio' ? Mic : MonitorUp

  const statusText = useMemo(() => {
    switch (status) {
      case 'requesting-permission':
        return '正在请求权限...'
      case 'recording':
        return `录制中 ${formatRecordingDuration(elapsedSeconds)}`
      case 'paused':
        return `已暂停 ${formatRecordingDuration(elapsedSeconds)}`
      case 'processing':
        return '正在整理录制结果...'
      case 'error':
        return errorMessage ?? '录制失败'
      default:
        return supported ? '准备开始录制' : '当前浏览器不支持录制'
    }
  }, [elapsedSeconds, errorMessage, status, supported])

  const isRecording = status === 'recording'
  const isRequesting = status === 'requesting-permission'
  const isPaused = status === 'paused'
  const primaryDisabled = !supported || status === 'requesting-permission' || status === 'processing' || isSaving
  const audioLevelPercent = Math.round(audioLevels.master * 100)
  const audioLevelDbLabel = formatMeterDb(audioLevels.master)
  const leftDbLabel = formatMeterDb(audioLevels.left)
  const rightDbLabel = formatMeterDb(audioLevels.right)
  const buttonHaloOpacity = 0.1 + audioLevels.master * 0.22
  const buttonGlowOpacity = 0.12 + audioLevels.master * 0.22
  const buttonInnerHighlightOpacity = 0.1 + audioLevels.master * 0.26
  const buttonShadowStrength = 0.18 + audioLevels.master * 0.18

  const applyMeterVisuals = useCallback(
    (leftDisplay: number, leftPeak: number, rightDisplay: number, rightPeak: number) => {
      if (!meterVisualRootRef.current) {
        return
      }

      const element = meterVisualRootRef.current
      element.style.setProperty('--meter-l', `${leftDisplay}%`)
      element.style.setProperty('--meter-l-peak', `${leftPeak}%`)
      element.style.setProperty('--meter-r', `${rightDisplay}%`)
      element.style.setProperty('--meter-r-peak', `${rightPeak}%`)
    },
    [],
  )

  const refreshAudioInputDevices = useCallback(async () => {
    if (kind !== 'audio' && kind !== 'screen') {
      return
    }

    setIsRefreshingDevices(true)
    try {
      const devices = await listAudioInputDevices()
      setAudioInputDevices(devices)
      setSelectedAudioInputDeviceId((previous) => {
        if (devices.length === 0) {
          return 'default'
        }
        if (previous === 'default') {
          return devices[0]?.deviceId ?? 'default'
        }
        return devices.some((device) => device.deviceId === previous)
          ? previous
          : (devices[0]?.deviceId ?? 'default')
      })
    } finally {
      setIsRefreshingDevices(false)
    }
  }, [kind])

  useEffect(() => {
    if (!open) {
      setSaveError(null)
      setIsSaving(false)
      setIsScreenMinimized(false)
      setFloatingButtonPosition(null)
      cancelRecording()
      reset()
    }
  }, [cancelRecording, open, reset])

  useEffect(() => {
    if (kind !== 'screen' || !isScreenMinimized) {
      return
    }

    setFloatingButtonPosition((previous) => previous ?? getDefaultFloatingPosition())
  }, [isScreenMinimized, kind])

  useEffect(() => {
    if (kind !== 'screen' || !isScreenMinimized) {
      return
    }

    const handleResize = () => {
      setFloatingButtonPosition((previous) =>
        clampFloatingPosition(previous ?? getDefaultFloatingPosition()),
      )
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isScreenMinimized, kind])

  useEffect(() => {
    if (kind !== 'screen') {
      return
    }

    const handlePointerMove = (event: PointerEvent) => {
      const drag = floatingDragRef.current
      if (!drag) {
        return
      }

      event.preventDefault()

      const nextPosition = clampFloatingPosition({
        x: drag.startPosition.x + (event.clientX - drag.startPointerX),
        y: drag.startPosition.y + (event.clientY - drag.startPointerY),
      })

      if (
        Math.abs(event.clientX - drag.startPointerX) > 4 ||
        Math.abs(event.clientY - drag.startPointerY) > 4
      ) {
        drag.moved = true
      }

      setFloatingButtonPosition(nextPosition)
    }

    const handlePointerUp = () => {
      floatingDragRef.current = null
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [kind])

  useEffect(() => {
    if (kind !== 'audio' || !open) {
      if (meterFrameRef.current !== null) {
        window.cancelAnimationFrame(meterFrameRef.current)
        meterFrameRef.current = null
      }
      meterAnimationRef.current = {
        left: { displayPercent: 0, peakPercent: 0, peakHoldSeconds: 0 },
        right: { displayPercent: 0, peakPercent: 0, peakHoldSeconds: 0 },
        lastTimestamp: 0,
      }
      meterTargetPercentRef.current = { left: 0, right: 0 }
      applyMeterVisuals(0, 0, 0, 0)
      return
    }

    const tick = (timestamp: number) => {
      const state = meterAnimationRef.current
      const deltaSeconds =
        state.lastTimestamp > 0 ? Math.min(0.05, (timestamp - state.lastTimestamp) / 1000) : 1 / 60
      state.lastTimestamp = timestamp

      const targets = meterTargetPercentRef.current
      animateMeterChannel(state.left, targets.left, deltaSeconds)
      animateMeterChannel(state.right, targets.right, deltaSeconds)
      applyMeterVisuals(
        state.left.displayPercent,
        state.left.peakPercent,
        state.right.displayPercent,
        state.right.peakPercent,
      )

      const shouldContinue =
        isRecording ||
        targets.left > 0.1 ||
        targets.right > 0.1 ||
        state.left.displayPercent > 0.1 ||
        state.right.displayPercent > 0.1 ||
        state.left.peakPercent > 0.1 ||
        state.right.peakPercent > 0.1

      if (shouldContinue) {
        meterFrameRef.current = window.requestAnimationFrame(tick)
        return
      }

      meterFrameRef.current = null
      state.lastTimestamp = 0
    }

    if (meterFrameRef.current === null) {
      meterFrameRef.current = window.requestAnimationFrame(tick)
    }

    return () => {
      if (meterFrameRef.current !== null) {
        window.cancelAnimationFrame(meterFrameRef.current)
        meterFrameRef.current = null
      }
    }
  }, [applyMeterVisuals, isRecording, kind, open])

  useEffect(() => {
    if (kind !== 'audio') {
      return
    }

    meterTargetPercentRef.current = {
      left: linearLevelToPercent(audioLevels.left),
      right: linearLevelToPercent(audioLevels.right),
    }
  }, [audioLevels.left, audioLevels.right, kind])

  useEffect(() => {
    if (!open || (kind !== 'audio' && kind !== 'screen')) {
      return
    }

    let cancelled = false

    void refreshAudioInputDevices().catch(() => {
      if (!cancelled) {
        setAudioInputDevices([])
      }
    })

    const mediaDevices = navigator.mediaDevices
    const handleDeviceChange = () => {
      void refreshAudioInputDevices().catch(() => undefined)
    }

    mediaDevices?.addEventListener?.('devicechange', handleDeviceChange)

    return () => {
      cancelled = true
      mediaDevices?.removeEventListener?.('devicechange', handleDeviceChange)
    }
  }, [kind, open, refreshAudioInputDevices])

  const handleStopAndSave = async () => {
    setSaveError(null)
    setIsSaving(true)
    try {
      const file = await stopRecording()
      await onSaveRecording(file)
      onOpenChange(false)
    } catch (error) {
      const message = error instanceof Error ? error.message : '保存录制结果失败。'
      if (message !== '录制已取消。') {
        setSaveError(message)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handlePrimaryAction = () => {
    if (isRecording) {
      void handleStopAndSave()
      return
    }

    if (isPaused) {
      try {
        resumeRecording()
      } catch (error) {
        const message = error instanceof Error ? error.message : '无法继续录制。'
        setSaveError(message)
      }
      return
    }

    if (kind === 'screen') {
      setIsScreenMinimized(true)
    }

    void startRecording({
      audioDeviceId:
        (kind === 'audio' || (kind === 'screen' && includeScreenMicrophone)) &&
        selectedAudioInputDeviceId !== 'default'
          ? selectedAudioInputDeviceId
          : null,
      includeMicrophone: kind === 'screen' ? includeScreenMicrophone : undefined,
    }).then(async (started) => {
      if (!started && kind === 'screen') {
        setIsScreenMinimized(false)
      }

      if (kind !== 'audio') {
        return
      }

      const devices = await listAudioInputDevices().catch(() => [])
      if (devices.length > 0) {
        setAudioInputDevices(devices)
      }
    })
  }

  const handlePauseResume = () => {
    try {
      if (isRecording) {
        pauseRecording()
        return
      }

      if (isPaused) {
        resumeRecording()
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '无法更新录制状态。'
      setSaveError(message)
    }
  }

  const handleFloatingPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (kind !== 'screen') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture?.(event.pointerId)

    floatingDragRef.current = {
      startPointerX: event.clientX,
      startPointerY: event.clientY,
      startPosition: floatingButtonPosition ?? getDefaultFloatingPosition(),
      moved: false,
    }
  }

  const handleFloatingExpand = () => {
    setIsScreenMinimized(false)
  }

  const stopFloatingPointerPropagation = (event: React.PointerEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const isDialogVisible = open && !(kind === 'screen' && isScreenMinimized)

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setIsScreenMinimized(false)
    }
    onOpenChange(nextOpen)
  }

  const canMinimizeScreenDialog = kind === 'screen' && (isRecording || isRequesting || status === 'processing')

  return (
    <>
      {kind === 'screen' && open && isScreenMinimized && (
        <div
          className="fixed z-[70] flex w-[236px] select-none items-center gap-2 rounded-2xl border border-white/10 bg-background/95 px-2 py-2 text-left shadow-[0_14px_42px_rgba(0,0,0,0.45)] ring-1 ring-black/30 backdrop-blur cursor-grab active:cursor-grabbing"
          style={{
            left: (floatingButtonPosition ?? getDefaultFloatingPosition()).x,
            top: (floatingButtonPosition ?? getDefaultFloatingPosition()).y,
            touchAction: 'none',
          }}
          onPointerDown={handleFloatingPointerDown}
        >
          <button
            type="button"
            onPointerDown={stopFloatingPointerPropagation}
            onClick={handleFloatingExpand}
            className="flex min-w-0 flex-1 flex-col items-start justify-center rounded-xl border border-white/8 bg-white/[0.03] px-3 py-2 text-left transition-colors hover:bg-white/[0.06]"
            aria-label="展开屏幕录制控制面板"
          >
            <span className="rounded-full border border-white/8 bg-white/[0.03] px-2 py-1 text-[10px] font-medium tracking-[0.14em] text-muted-foreground/80">
              {isPaused ? 'PAUSE' : isRequesting ? 'WAIT' : 'REC'}
            </span>
            <span className="mt-1 min-w-[52px] text-sm font-medium tabular-nums text-foreground">
              {isRecording || isPaused ? formatRecordingDuration(elapsedSeconds) : '00:00'}
            </span>
          </button>

          {(isRecording || isPaused) && (
            <>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-9 w-9 shrink-0 rounded-full"
                onPointerDown={stopFloatingPointerPropagation}
                onClick={handlePauseResume}
                aria-label={isPaused ? '继续录制' : '暂停录制'}
              >
                {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="h-9 w-9 shrink-0 rounded-full text-red-400 hover:text-red-300"
                onPointerDown={stopFloatingPointerPropagation}
                onClick={() => {
                  void handleStopAndSave()
                }}
                aria-label="停止并保存录制"
              >
                <Square className="h-4 w-4 fill-current" />
              </Button>
            </>
          )}
        </div>
      )}

      <Dialog open={isDialogVisible} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        {kind === 'screen' && canMinimizeScreenDialog && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-12 top-3 h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setIsScreenMinimized(true)}
          >
            收起
          </Button>
        )}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(kind === 'audio' || (kind === 'screen' && includeScreenMicrophone)) && (
            <div className="space-y-2 rounded-lg border border-border/70 bg-secondary/10 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">
                  {kind === 'audio' ? '录音设备' : '麦克风设备'}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => {
                    void refreshAudioInputDevices()
                  }}
                  disabled={isRecording || status === 'requesting-permission' || status === 'processing' || isSaving || isRefreshingDevices}
                >
                  <RefreshCw className={cn('mr-1 h-3.5 w-3.5', isRefreshingDevices && 'animate-spin')} />
                  刷新设备
                </Button>
              </div>
              <Select
                value={selectedAudioInputDeviceId}
                onValueChange={setSelectedAudioInputDeviceId}
                disabled={isRecording || status === 'requesting-permission' || status === 'processing' || isSaving || isRefreshingDevices}
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择录音设备" />
                </SelectTrigger>
                <SelectContent>
                  {audioInputDevices.length > 0 ? (
                    audioInputDevices.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="default">默认录音设备</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {kind === 'audio'
                  ? '开始录音前可切换麦克风。若列表名称不完整，通常在首次授权后会自动更新。'
                  : '如果要把旁白一起录进屏幕素材，可在开始前选择要使用的麦克风。'}
              </p>
            </div>
          )}

          {kind === 'audio' && (
            <div className="rounded-lg border border-border/70 bg-secondary/10 p-6">
              <div className="grid justify-center gap-6 sm:grid-cols-[128px_148px] sm:items-center">
                <div className="flex flex-col items-center justify-center">
                  <div className="relative flex h-28 w-28 items-center justify-center">
                    {isRecording && (
                      <>
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute h-24 w-24 rounded-full border border-red-400/35"
                          style={{
                            opacity: buttonHaloOpacity,
                            transition: 'opacity 160ms ease-out',
                          }}
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute h-[108px] w-[108px] rounded-full bg-red-500/15 blur-md"
                          style={{
                            opacity: buttonGlowOpacity,
                            transition: 'opacity 180ms ease-out',
                          }}
                        />
                      </>
                    )}
                    <button
                      type="button"
                      onClick={handlePrimaryAction}
                      disabled={primaryDisabled}
                      className={cn(
                        'relative z-10 flex h-24 w-24 items-center justify-center rounded-full transition-transform disabled:cursor-not-allowed disabled:opacity-50',
                        isRecording
                          ? 'bg-red-500 text-white shadow-[0_0_0_8px_rgba(239,68,68,0.08)]'
                          : 'bg-red-500/90 text-white hover:scale-[1.02]',
                      )}
                      style={
                        isRecording
                          ? {
                              boxShadow: `0 0 0 8px rgba(239,68,68,0.08), 0 0 24px rgba(239,68,68,${buttonShadowStrength})`,
                              transition: 'box-shadow 150ms ease-out',
                            }
                          : undefined
                      }
                      aria-label={isRecording ? '停止并保存录音' : '开始录音'}
                    >
                      {isRecording && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-[3px] rounded-full bg-gradient-to-b from-white via-white/20 to-transparent"
                          style={{
                            opacity: buttonInnerHighlightOpacity,
                            transition: 'opacity 140ms ease-out',
                          }}
                        />
                      )}
                      {status === 'requesting-permission' || status === 'processing' || isSaving ? (
                        <Loader2 className="relative z-10 h-7 w-7 animate-spin" />
                      ) : (
                        <Mic className="relative z-10 h-8 w-8" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="w-[156px] rounded-md border border-border/50 bg-black/40 px-3 py-3 shadow-[inset_0_1px_4px_rgba(0,0,0,0.35)]">
                  <div className="mb-2 text-center text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70">
                    输入
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="relative h-40 w-10 shrink-0">
                    {AUDIO_METER_SCALE_MARKS.map((mark) => {
                      const bottom = `${dbMarkToPercent(mark)}%`
                      return (
                        <div key={mark} className="absolute inset-x-0" style={{ bottom }}>
                          <div className="absolute left-0 right-5 h-px bg-border/30" />
                          <span className="absolute right-0 -translate-y-1/2 text-[10px] font-mono text-muted-foreground/55">
                            {mark}
                          </span>
                        </div>
                      )
                    })}
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div ref={meterVisualRootRef} className="flex items-end gap-[3px]">
                        {['L', 'R'].map((channel) => (
                          <div key={channel} className="flex flex-col items-center gap-1">
                            <div className="relative h-40 w-[14px] overflow-hidden rounded-[2px] border border-border/50 bg-[#08090b] shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)]">
                              <div
                                className="pointer-events-none absolute inset-0"
                                style={{ background: RECORDING_METER_UNLIT_BG }}
                              />
                              <div
                                className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1be255] via-[#f5e146] to-[#ff6633] transition-[height] duration-75 ease-out"
                                style={{
                                  height: channel === 'L' ? 'var(--meter-l, 0%)' : 'var(--meter-r, 0%)',
                                  maskImage: RECORDING_METER_SEGMENT_MASK,
                                  WebkitMaskImage: RECORDING_METER_SEGMENT_MASK,
                                }}
                              />
                              <div
                                className="absolute inset-x-0 h-[3px] bg-white/85 shadow-[0_0_5px_rgba(255,255,255,0.5)] transition-[bottom] duration-100 ease-out"
                                style={{
                                  bottom:
                                    channel === 'L'
                                      ? 'calc(var(--meter-l-peak, 0%) - 1px)'
                                      : 'calc(var(--meter-r-peak, 0%) - 1px)',
                                  maskImage: RECORDING_METER_SEGMENT_MASK,
                                  WebkitMaskImage: RECORDING_METER_SEGMENT_MASK,
                                }}
                              />
                            </div>
                            <div className="text-[10px] font-mono text-muted-foreground/70">{channel}</div>
                          </div>
                        ))}
                      </div>
                      <div className="grid w-full grid-cols-2 gap-x-3 gap-y-1 text-center text-[10px] font-mono tabular-nums text-muted-foreground">
                        <div className="text-muted-foreground/70">L</div>
                        <div className="text-muted-foreground/70">R</div>
                        <div className="min-w-[58px] text-foreground">{leftDbLabel}</div>
                        <div className="min-w-[58px] text-foreground">{rightDbLabel}</div>
                      </div>
                      <div className="w-full border-t border-white/5 pt-1 text-center text-[11px] font-mono tabular-nums text-foreground">
                        <span className="inline-block min-w-[68px]">{audioLevelDbLabel}</span>
                      </div>
                      <div className="w-full text-center text-[10px] tabular-nums text-muted-foreground">
                        <span className="inline-block min-w-[44px]">{audioLevelPercent}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="mt-4 text-center text-sm font-medium text-foreground">
                {isRecording ? '正在录音，点击红色按钮可停止并保存' : '点击红色按钮开始录音'}
              </p>
              <p className="mt-1 text-center text-xs text-muted-foreground">
                {isRecording
                  ? `当前声量反馈 ${formatRecordingDuration(elapsedSeconds)}`
                  : '录音时会显示接近主控表样式的实时输入电平。'}
              </p>
            </div>
          )}

          {kind === 'screen' && (
            <div className="rounded-lg border border-border/70 bg-secondary/10 p-6">
              <div className="mb-5 flex items-center justify-between rounded-md border border-border/60 bg-black/20 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-foreground">同时录制麦克风</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    开启后会把当前麦克风声音一起写入录屏结果。
                  </p>
                </div>
                <Switch
                  checked={includeScreenMicrophone}
                  onCheckedChange={setIncludeScreenMicrophone}
                  disabled={isRecording || isRequesting || status === 'processing' || isSaving}
                  aria-label="切换是否同时录制麦克风"
                />
              </div>

              <div className="flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={handlePrimaryAction}
                  disabled={primaryDisabled}
                  className={cn(
                    'relative flex h-24 w-24 items-center justify-center rounded-full transition-transform disabled:cursor-not-allowed disabled:opacity-50',
                    isRecording || isPaused
                      ? 'bg-red-500 text-white shadow-[0_0_0_8px_rgba(239,68,68,0.08)]'
                      : 'bg-sky-500/90 text-white shadow-[0_0_0_8px_rgba(14,165,233,0.08)] hover:scale-[1.02]',
                  )}
                  aria-label={isRecording ? '停止并保存录屏' : isPaused ? '继续录屏' : '开始录屏'}
                >
                  {status === 'requesting-permission' || status === 'processing' || isSaving ? (
                    <Loader2 className="h-7 w-7 animate-spin" />
                  ) : isPaused ? (
                    <Play className="h-8 w-8" />
                  ) : (
                    <MonitorUp className="h-8 w-8" />
                  )}
                </button>

                {(isRecording || isPaused) && (
                  <div className="mt-4 flex items-center gap-3">
                    <Button type="button" variant="secondary" onClick={handlePauseResume}>
                      {isPaused ? '继续录制' : '暂停录制'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-red-500/30 text-red-300 hover:text-red-200"
                      onClick={() => {
                        void handleStopAndSave()
                      }}
                    >
                      停止并保存
                    </Button>
                  </div>
                )}

                <p className="mt-4 text-center text-sm font-medium text-foreground">
                  {isRecording
                    ? '正在录屏，可暂停、继续或停止并保存'
                    : isPaused
                      ? '录屏已暂停，可继续录制或直接停止保存'
                      : '点击按钮后选择要共享的屏幕或窗口'}
                </p>
                <p className="mt-1 text-center text-xs text-muted-foreground">
                  {isRecording || isPaused
                    ? `当前录制时长 ${formatRecordingDuration(elapsedSeconds)}`
                    : includeScreenMicrophone
                      ? '浏览器会弹出系统共享面板，并在录屏时同时采集当前麦克风。'
                      : '浏览器会弹出系统共享面板，当前仅录制屏幕画面。'}
                </p>

                <div className="mt-5 w-full max-w-sm rounded-md border border-border/50 bg-black/20 px-4 py-3 text-xs text-muted-foreground">
                  <p>开始后请在浏览器共享面板中选择整个屏幕、窗口或标签页。</p>
                  <p className="mt-2">
                    {includeScreenMicrophone
                      ? '停止共享后会自动结束录制，并把屏幕画面与麦克风声音一起写入当前项目媒体库。'
                      : '停止共享后会自动结束录制，并把结果写入当前项目媒体库。'}
                  </p>
                  <p className="mt-2 text-amber-200/90">
                    如果录制时发现画面整体发白，通常是系统 HDR、浏览器硬件加速或显卡显示增强导致，建议先关闭 HDR，或改为录制单个窗口后再试。
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-border/70 bg-secondary/20 p-4">
            <p className="text-sm font-medium text-foreground">当前状态</p>
            <p className="mt-1 text-sm text-muted-foreground">{statusText}</p>
            {kind === 'audio' && (
              <p className="mt-2 text-xs text-muted-foreground">
                建议在安静环境下录制，单次先控制在 10 分钟以内。
              </p>
            )}
          </div>

          {(errorMessage || saveError) && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {saveError ?? errorMessage}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleDialogOpenChange(false)}
            disabled={status === 'processing' || isSaving}
          >
            取消
          </Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
    </>
  )
}